/**
 * @qrqy/shared/qrConfig
 *
 * QR code configuration, scannability guardrails, and print spec validation.
 *
 * QRQY's QR codes are printed on physical garments — so scannability isn't just
 * a nice-to-have, it's the core product promise. These rules encode hard-won
 * knowledge about what makes a garment QR code work in the real world.
 *
 * This module has NO external dependencies and works in:
 *   - Cloudflare Workers runtime
 *   - Vite-bundled browser apps (dashboard QR editor)
 *   - Node.js (for testing)
 */

/**
 * Default QR code generation options.
 * These are passed to the `qrcode` library (or equivalent) in M3.
 * Error correction level H is required for garment printing — fabric texture
 * and wear will damage some modules. H allows up to 30% of modules to be
 * unreadable and still scan.
 *
 * @type {{
 *   errorCorrectionLevel: 'L'|'M'|'Q'|'H',
 *   margin: number,
 *   color: { dark: string, light: string }
 * }}
 */
export const QR_DEFAULTS = {
  /**
   * Error correction level.
   * H = ~30% of modules can be damaged and still scan.
   * Required for garments — fabric weave, washing, and wear will obscure modules.
   * Using L or M would cause real-world scan failures on aged garments.
   */
  errorCorrectionLevel: 'H',

  /**
   * Quiet zone in modules (blank border around the QR code).
   * ISO 18004 requires a minimum of 4 modules. Never go below this —
   * scanners use the quiet zone to locate the QR code boundaries.
   */
  margin: 4,

  /**
   * Color configuration.
   * Dark modules must be dark (#000000), light modules must be white (#FFFFFF).
   * Do not invert or use brand colors directly — see BRAND_QR_GUIDELINES.
   */
  color: {
    dark: '#000000',
    light: '#FFFFFF',
  },
};

/**
 * Standard print size presets in pixels (for screen/digital preview).
 * These correspond to physical print sizes at 300 DPI:
 *   small  → ~17mm (business card QR, marginal scannability)
 *   medium → ~34mm (recommended minimum for apparel)
 *   large  → ~51mm (optimal for all garments)
 *
 * @type {{ small: number, medium: number, large: number }}
 */
export const QR_SIZE_PRESETS = {
  /** 200px — minimum viable for testing only. Not recommended for garment print. */
  small: 200,
  /** 400px — recommended minimum for apparel. Sufficient for H error correction. */
  medium: 400,
  /** 600px — ideal for hoodies and oversized pieces. Maximum scannability. */
  large: 600,
};

/**
 * Brand QR design standards for QRQY garments.
 * These are editorial + technical guidelines for the POD provider and design team.
 *
 * @type {object}
 */
export const BRAND_QR_GUIDELINES = {
  /**
   * Contrast requirement: the dark modules must have a contrast ratio of at least 3:1
   * against the light modules when measured by WCAG 2.1 standards.
   * In practice: always use black on white. Any brand color overlay must meet this ratio.
   */
  minimumContrastRatio: 3,

  /**
   * Color inversion prohibition:
   * White modules on dark background (inverted QR) scans unreliably on
   * many devices. All QRQY QR codes must use dark-on-light, never inverted.
   */
  invertedColorsAllowed: false,

  /**
   * Quiet zone requirement: minimum 4 modules of blank space on all sides.
   * ISO 18004 mandates this. Any design that overlaps the quiet zone will cause
   * scan failures at close or acute angles.
   */
  minimumQuietZoneModules: 4,

  /**
   * Error correction level: always H for garment printing.
   * Lower levels will cause real-world scan failures as the garment ages.
   */
  requiredErrorCorrectionLevel: 'H',

  /**
   * Minimum physical print size: 25mm on the shortest side.
   * Below this, modules become too small for most phone cameras under
   * typical scanning conditions (arm's length, moving subject).
   */
  minimumPhysicalSizeMM: 25,

  /**
   * Maximum URL length for optimal scan speed: 300 characters.
   * Longer URLs increase QR code density, making individual modules smaller
   * and increasing the probability of a misread at normal scanning distances.
   * Note: the validator allows up to 2048 chars — this is a quality guideline, not a hard block.
   */
  recommendedMaxUrlLength: 300,

  /**
   * Editorial guidance: The QR code IS the design element.
   * It should be presented at full bleed on the garment zone, not treated as a label.
   * Minimum recommended garment zone: 10cm × 10cm.
   */
  editorialNote:
    'The QR code is not an accent — it is the statement. ' +
    'Size it large enough that scanning it from across a room is effortless. ' +
    'Quiet zone is sacred. Do not encroach on it with brand elements.',
};

/**
 * Check a destination URL for scannability concerns.
 *
 * This is informational/advisory — it produces warnings, not hard blocks.
 * The URL may still be valid (per validateDestinationUrl) but have properties
 * that reduce real-world scan reliability.
 *
 * @param {string} destinationUrl - The destination URL to check
 * @returns {{ scannable: boolean, warnings: string[] }}
 *
 * @example
 * checkScannability('https://example.com/very-long-path?utm_source=qrqy&utm_medium=garment')
 * // → { scannable: true, warnings: ['Tracking parameters are fine but add URL length.', ...] }
 */
export function checkScannability(destinationUrl) {
  const warnings = [];

  if (!destinationUrl || typeof destinationUrl !== 'string') {
    return { scannable: false, warnings: ['No URL provided.'] };
  }

  const url = destinationUrl.trim();

  // Length warning — longer URL = higher QR density = smaller modules = harder to scan
  if (url.length > 500) {
    warnings.push(
      `Long URLs reduce scan reliability (${url.length} chars). ` +
      'Consider using a URL shortener or a cleaner path structure.'
    );
  } else if (url.length > BRAND_QR_GUIDELINES.recommendedMaxUrlLength) {
    warnings.push(
      `URL is ${url.length} characters (recommended max: ${BRAND_QR_GUIDELINES.recommendedMaxUrlLength}). ` +
      'This will increase QR code density — test on actual garment before production print.'
    );
  }

  // Tracking parameter warnings
  // Common tracking params that add length without changing content
  const trackingParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid', 'gclid', 'msclkid', 'igshid'];
  try {
    const parsed = new URL(url);
    const foundTracking = [];
    for (const param of trackingParams) {
      if (parsed.searchParams.has(param)) {
        foundTracking.push(param);
      }
    }
    if (foundTracking.length > 0) {
      warnings.push(
        `Tracking parameters detected (${foundTracking.join(', ')}). ` +
        'These are fine for analytics but add URL length. Consider a clean destination URL and use server-side attribution instead.'
      );
    }

    // HTTP warning (should already be blocked by validator, but extra safety)
    if (parsed.protocol === 'http:') {
      warnings.push(
        'HTTP URLs may show browser security warnings to scanners. Use HTTPS.'
      );
    }

    // Very deep path warning
    const pathSegments = parsed.pathname.split('/').filter(Boolean);
    if (pathSegments.length > 6) {
      warnings.push(
        `Deep URL path (${pathSegments.length} segments) adds length. ` +
        'Shallower paths scan more reliably and are easier to change later.'
      );
    }

    // Fragment-only concerns
    if (parsed.hash && !parsed.pathname.replace('/', '')) {
      warnings.push(
        'URL contains only a hash fragment. Ensure the base page loads correctly before the fragment is applied.'
      );
    }
  } catch {
    // If URL parsing fails here, the URL validator should have already caught it
    warnings.push('Could not fully analyze URL for scannability — ensure the URL is valid.');
  }

  return {
    scannable: true, // Warnings don't block — the URL can still be set
    warnings,
  };
}

/**
 * Validate a physical print size against QRQY's minimum scannability spec.
 *
 * @param {number} mmSize - The shortest dimension of the QR code print area, in millimeters
 * @returns {{
 *   printable: boolean,
 *   minRecommendedMM: number,
 *   message: string
 * }}
 *
 * @example
 * validatePrintSize(30)
 * // → { printable: true, minRecommendedMM: 25, message: 'Print size is within spec.' }
 *
 * validatePrintSize(15)
 * // → { printable: false, minRecommendedMM: 25, message: 'Print size (15mm) is below the minimum...' }
 */
export function validatePrintSize(mmSize) {
  const MIN_MM = BRAND_QR_GUIDELINES.minimumPhysicalSizeMM;

  if (typeof mmSize !== 'number' || isNaN(mmSize) || mmSize <= 0) {
    return {
      printable: false,
      minRecommendedMM: MIN_MM,
      message: 'Invalid print size. Provide a positive number in millimeters.',
    };
  }

  if (mmSize < MIN_MM) {
    return {
      printable: false,
      minRecommendedMM: MIN_MM,
      message:
        `Print size (${mmSize}mm) is below the minimum ${MIN_MM}mm required for reliable scanning. ` +
        `At this size, modules become too small for most phone cameras at arm's length. ` +
        `Increase the print area or use the recommended minimum of ${MIN_MM}mm.`,
    };
  }

  if (mmSize < 40) {
    return {
      printable: true,
      minRecommendedMM: MIN_MM,
      message:
        `Print size (${mmSize}mm) meets the minimum but is marginal. ` +
        `For garments, 40mm+ gives the best scan success rate across all devices. ` +
        `Consider increasing the print area if the design allows.`,
    };
  }

  return {
    printable: true,
    minRecommendedMM: MIN_MM,
    message: `Print size (${mmSize}mm) is within spec. Expected scan success rate is high.`,
  };
}
